import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import apiClient from '../services/api';
import './PublishingSettings.css';

interface PublishingMetadata {
  memoir_id: string;
  title: string;
  author_name: string | null;
  subtitle: string | null;
  back_cover_text: string | null;
  author_bio: string | null;
  cover_image_url: string | null;
  back_cover_image_url: string | null;
  dedication: string | null;
  isbn: string | null;
  publisher: string | null;
  publication_date: string | null;
  copyright_text: string | null;
  // Front cover element positions
  title_position: 'top' | 'middle' | 'bottom' | null;
  subtitle_position: 'top' | 'middle' | 'bottom' | null;
  author_name_position: 'top' | 'middle' | 'bottom' | null;
  // Back cover element positions
  back_cover_text_position: 'top' | 'middle' | 'bottom' | null;
  author_bio_position: 'top' | 'middle' | 'bottom' | null;
  publishing_info_position: 'top' | 'middle' | 'bottom' | null;
  // Front cover element visibility
  title_visible: boolean | null;
  subtitle_visible: boolean | null;
  author_name_visible: boolean | null;
  // Back cover element visibility
  back_cover_text_visible: boolean | null;
  author_bio_visible: boolean | null;
  publishing_info_visible: boolean | null;
  // Cover font colors
  front_cover_font_color: string | null;
  back_cover_font_color: string | null;
}

type TextPosition = 'top' | 'middle' | 'bottom';

// Compact position selector with visual icons and visibility toggle
const PositionSelector: React.FC<{
  value: TextPosition;
  onChange: (pos: TextPosition) => void;
  visible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}> = ({ value, onChange, visible = true, onVisibilityChange }) => (
  <div className="position-selector-compact">
    <button
      type="button"
      className={`pos-btn ${value === 'top' ? 'active' : ''} ${!visible ? 'disabled' : ''}`}
      onClick={() => onChange('top')}
      title="Top"
      disabled={!visible}
    >
      <svg width="12" height="12" viewBox="0 0 12 12">
        <rect x="2" y="1" width="8" height="2" fill="currentColor"/>
        <rect x="4" y="4" width="4" height="1" fill="currentColor" opacity="0.4"/>
        <rect x="4" y="6" width="4" height="1" fill="currentColor" opacity="0.4"/>
      </svg>
    </button>
    <button
      type="button"
      className={`pos-btn ${value === 'middle' ? 'active' : ''} ${!visible ? 'disabled' : ''}`}
      onClick={() => onChange('middle')}
      title="Middle"
      disabled={!visible}
    >
      <svg width="12" height="12" viewBox="0 0 12 12">
        <rect x="4" y="1" width="4" height="1" fill="currentColor" opacity="0.4"/>
        <rect x="2" y="5" width="8" height="2" fill="currentColor"/>
        <rect x="4" y="10" width="4" height="1" fill="currentColor" opacity="0.4"/>
      </svg>
    </button>
    <button
      type="button"
      className={`pos-btn ${value === 'bottom' ? 'active' : ''} ${!visible ? 'disabled' : ''}`}
      onClick={() => onChange('bottom')}
      title="Bottom"
      disabled={!visible}
    >
      <svg width="12" height="12" viewBox="0 0 12 12">
        <rect x="4" y="5" width="4" height="1" fill="currentColor" opacity="0.4"/>
        <rect x="4" y="7" width="4" height="1" fill="currentColor" opacity="0.4"/>
        <rect x="2" y="9" width="8" height="2" fill="currentColor"/>
      </svg>
    </button>
    {onVisibilityChange && (
      <button
        type="button"
        className={`pos-btn visibility-btn ${!visible ? 'active hidden' : ''}`}
        onClick={() => onVisibilityChange(!visible)}
        title={visible ? "Hide from cover" : "Show on cover"}
      >
        <svg width="12" height="12" viewBox="0 0 12 12">
          {visible ? (
            /* Eye icon - visible */
            <>
              <ellipse cx="6" cy="6" rx="5" ry="3" fill="none" stroke="currentColor" strokeWidth="1.2"/>
              <circle cx="6" cy="6" r="1.5" fill="currentColor"/>
            </>
          ) : (
            /* X icon - hidden */
            <>
              <line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </>
          )}
        </svg>
      </button>
    )}
  </div>
);

// Cover font color options
type CoverColor = 'white' | 'lightgrey' | 'darkgrey' | 'black' | 'red' | 'green' | 'blue' | 'orange' | 'brown' | 'pink' | 'purple';

const COVER_COLORS: { value: CoverColor; label: string; hex: string }[] = [
  { value: 'white', label: 'White', hex: '#ffffff' },
  { value: 'lightgrey', label: 'Light Grey', hex: '#b0b0b0' },
  { value: 'darkgrey', label: 'Dark Grey', hex: '#505050' },
  { value: 'black', label: 'Black', hex: '#000000' },
  { value: 'red', label: 'Red', hex: '#dc2626' },
  { value: 'green', label: 'Green', hex: '#16a34a' },
  { value: 'blue', label: 'Blue', hex: '#2563eb' },
  { value: 'orange', label: 'Orange', hex: '#ea580c' },
  { value: 'brown', label: 'Brown', hex: '#78350f' },
  { value: 'pink', label: 'Pink', hex: '#db2777' },
  { value: 'purple', label: 'Purple', hex: '#7c3aed' },
];

// Color selector component for cover text colors
const ColorSelector: React.FC<{
  value: CoverColor;
  onChange: (color: CoverColor) => void;
  label: string;
}> = ({ value, onChange, label }) => (
  <div className="color-selector">
    <label>{label}</label>
    <div className="color-options">
      {COVER_COLORS.map((color) => (
        <button
          key={color.value}
          type="button"
          className={`color-btn ${value === color.value ? 'active' : ''}`}
          style={{ backgroundColor: color.hex }}
          onClick={() => onChange(color.value)}
          title={color.label}
        />
      ))}
    </div>
  </div>
);

const PublishingSettings: React.FC = () => {
  const { memoirId } = useParams<{ memoirId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [metadata, setMetadata] = useState<PublishingMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState<{front: boolean, back: boolean}>({
    front: false,
    back: false
  });
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState<{front: boolean, back: boolean}>({
    front: false,
    back: false
  });
  const [uploadingCover, setUploadingCover] = useState<{front: boolean, back: boolean}>({
    front: false,
    back: false
  });
  const [coverPrompts, setCoverPrompts] = useState<{front: string, back: string}>({
    front: '',
    back: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    loadPublishingData();
  }, [memoirId]);

  const loadPublishingData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getPublishingMetadata(memoirId!);
      setMetadata(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load publishing settings');
      console.error('Error loading publishing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePrompt = async (coverType: 'front' | 'back') => {
    if (!metadata) return;

    setIsGeneratingPrompt(prev => ({ ...prev, [coverType]: true }));
    setError(null);

    try {
      const result = await apiClient.generateCoverPrompt(memoirId!, coverType);
      setCoverPrompts(prev => ({ ...prev, [coverType]: result.generated_prompt }));
      setSaveMessage(`AI prompt generated! Usage remaining: ${result.usage_remaining}`);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || `Failed to generate ${coverType} cover prompt`;
      setError(errorMsg);
    } finally {
      setIsGeneratingPrompt(prev => ({ ...prev, [coverType]: false }));
    }
  };

  const generateCover = async (coverType: 'front' | 'back') => {
    if (!metadata) return;

    // Check if cover already exists
    const existingUrl = coverType === 'front' ? metadata.cover_image_url : metadata.back_cover_image_url;
    const isRegenerate = !!existingUrl;

    // Get the custom prompt for this cover type
    const customPrompt = coverType === 'front' ? coverPrompts.front : coverPrompts.back;

    setIsGenerating(prev => ({ ...prev, [coverType]: true }));
    setError(null);

    try {
      const result = await apiClient.generateCoverImage(memoirId!, coverType, isRegenerate, customPrompt || undefined);
      await loadPublishingData(); // Reload to get new image URL
      setSaveMessage(`${coverType} cover generated successfully! Credits remaining: ${result.usage_info?.credits_remaining || result.usage_remaining}`);
      setTimeout(() => setSaveMessage(null), 5000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || `Failed to generate ${coverType} cover`;
      setError(errorMsg);
    } finally {
      setIsGenerating(prev => ({ ...prev, [coverType]: false }));
    }
  };

  const handleCoverUpload = async (coverType: 'front' | 'back') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';

    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = target.files;
      const file = files?.[0];
      if (!file) return;

      // Client-side validation
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        setError('File size exceeds 50MB limit. Please choose a smaller image.');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPG, JPEG, PNG, and WebP files are allowed for cover images.');
        return;
      }

      setUploadingCover(prev => ({ ...prev, [coverType]: true }));
      setError(null);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const result = await apiClient.uploadCoverImage(memoirId!, coverType, formData);
        await loadPublishingData(); // Reload to get new image URL

        const fileSizeMB = (result.file_size / (1024 * 1024)).toFixed(2);
        setSaveMessage(`${coverType} cover uploaded successfully! File size: ${fileSizeMB}MB (${result.optimization_ratio}% optimized)`);
        setTimeout(() => setSaveMessage(null), 5000);
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || `Failed to upload ${coverType} cover`;
        setError(errorMsg);
      } finally {
        setUploadingCover(prev => ({ ...prev, [coverType]: false }));
      }
    };

    input.click();
  };

  const saveMetadata = async () => {
    if (!metadata) return;

    setSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      const updated = await apiClient.updatePublishingMetadata(memoirId!, metadata);
      setMetadata(updated);
      setSaveMessage('Publishing settings saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to save publishing settings';
      setError(errorMsg);
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof PublishingMetadata, value: string | boolean | null) => {
    if (!metadata) return;
    setMetadata({ ...metadata, [field]: value });
  };

  if (loading) {
    return <div className="publishing-settings loading">Loading publishing settings...</div>;
  }

  if (!metadata) {
    return <div className="publishing-settings error">Failed to load publishing settings</div>;
  }

  const logoSmall = theme === 'dark'
    ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark_small.png`
    : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant_small.png`;

  return (
    <div className="publishing-settings">
      <header className="publishing-header">
        <div className="header-content">
          <div className="header-left">
            <img
              src={logoSmall}
              alt="Debriefr"
              style={{ height: '40px', cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            />
          </div>
          <div className="header-right">
            <button
              className="save-btn primary"
              onClick={saveMetadata}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button className="btn btn-outline" onClick={() => navigate(`/memoir/${memoirId}`)}>
              ← Back to Book
            </button>
          </div>
        </div>
      </header>

      <div className="publishing-title-section">
        <h1>Publishing & Cover Settings</h1>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {saveMessage && (
        <div className="alert alert-success">
          {saveMessage}
        </div>
      )}

      <div className="publishing-content">
        {/* Covers Container - Side by Side */}
        <div className="covers-container">
          {/* Front Cover Section */}
          <section className="cover-section">
            <h2>Front Cover</h2>

            <div className="cover-preview-container">
              <div className="cover-preview front-cover">
                {metadata.cover_image_url ? (
                  <div
                    className="cover-image-preview"
                    style={{ backgroundImage: `url(${metadata.cover_image_url})` }}
                  >
                    <div
                      className="cover-overlay-preview zone-layout"
                      style={{ '--preview-text-color': COVER_COLORS.find(c => c.value === (metadata.front_cover_font_color || 'white'))?.hex || '#ffffff' } as React.CSSProperties}
                    >
                      <div className="preview-zone-top">
                        {metadata.title_visible !== false && (metadata.title_position || 'middle') === 'top' && (
                          <div className="preview-title">{metadata.title}</div>
                        )}
                        {metadata.subtitle_visible !== false && metadata.subtitle && (metadata.subtitle_position || 'middle') === 'top' && (
                          <div className="preview-subtitle">{metadata.subtitle}</div>
                        )}
                        {metadata.author_name_visible !== false && (metadata.author_name_position || 'bottom') === 'top' && (
                          <div className="preview-author">by {metadata.author_name || 'Author Name'}</div>
                        )}
                      </div>
                      <div className="preview-zone-middle">
                        {metadata.title_visible !== false && (metadata.title_position || 'middle') === 'middle' && (
                          <div className="preview-title">{metadata.title}</div>
                        )}
                        {metadata.subtitle_visible !== false && metadata.subtitle && (metadata.subtitle_position || 'middle') === 'middle' && (
                          <div className="preview-subtitle">{metadata.subtitle}</div>
                        )}
                        {metadata.author_name_visible !== false && (metadata.author_name_position || 'bottom') === 'middle' && (
                          <div className="preview-author">by {metadata.author_name || 'Author Name'}</div>
                        )}
                      </div>
                      <div className="preview-zone-bottom">
                        {metadata.title_visible !== false && (metadata.title_position || 'middle') === 'bottom' && (
                          <div className="preview-title">{metadata.title}</div>
                        )}
                        {metadata.subtitle_visible !== false && metadata.subtitle && (metadata.subtitle_position || 'middle') === 'bottom' && (
                          <div className="preview-subtitle">{metadata.subtitle}</div>
                        )}
                        {metadata.author_name_visible !== false && (metadata.author_name_position || 'bottom') === 'bottom' && (
                          <div className="preview-author">by {metadata.author_name || 'Author Name'}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-cover-placeholder">
                    <p>No front cover</p>
                    <p className="placeholder-hint">Generate below</p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <div className="field-with-position">
                <label htmlFor="book_title">Book Title</label>
                <PositionSelector
                  value={(metadata.title_position || 'middle') as TextPosition}
                  onChange={(pos) => updateField('title_position', pos)}
                  visible={metadata.title_visible !== false}
                  onVisibilityChange={(v) => updateField('title_visible', v)}
                />
              </div>
              <input
                id="book_title"
                type="text"
                value={metadata.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Enter book title"
              />
            </div>

            <div className="form-group">
              <div className="field-with-position">
                <label htmlFor="subtitle">Subtitle</label>
                <PositionSelector
                  value={(metadata.subtitle_position || 'middle') as TextPosition}
                  onChange={(pos) => updateField('subtitle_position', pos)}
                  visible={metadata.subtitle_visible !== false}
                  onVisibilityChange={(v) => updateField('subtitle_visible', v)}
                />
              </div>
              <input
                id="subtitle"
                type="text"
                value={metadata.subtitle || ''}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="Optional subtitle"
              />
            </div>

            <div className="form-group">
              <div className="field-with-position">
                <label htmlFor="author_name">Author Name</label>
                <PositionSelector
                  value={(metadata.author_name_position || 'bottom') as TextPosition}
                  onChange={(pos) => updateField('author_name_position', pos)}
                  visible={metadata.author_name_visible !== false}
                  onVisibilityChange={(v) => updateField('author_name_visible', v)}
                />
              </div>
              <input
                id="author_name"
                type="text"
                value={metadata.author_name || ''}
                onChange={(e) => updateField('author_name', e.target.value)}
                placeholder="Enter author name"
              />
            </div>

            <ColorSelector
              value={(metadata.front_cover_font_color || 'white') as CoverColor}
              onChange={(color) => updateField('front_cover_font_color', color)}
              label="Text Color"
            />

            <div className="form-group">
              <label htmlFor="front_cover_prompt">AI Image Prompt (Optional)</label>
              <textarea
                id="front_cover_prompt"
                rows={3}
                value={coverPrompts.front}
                onChange={(e) => setCoverPrompts(prev => ({ ...prev, front: e.target.value }))}
                placeholder="Generate AI prompt from memoir or leave empty for default..."
              />
            </div>

            <div className="cover-buttons-row">
              <button
                className="generate-btn"
                onClick={() => generatePrompt('front')}
                disabled={isGeneratingPrompt.front}
              >
                {isGeneratingPrompt.front ? 'Generating...' : 'AI Prompt (1 credit)'}
              </button>
              <button
                className="generate-btn"
                onClick={() => generateCover('front')}
                disabled={isGenerating.front}
              >
                {isGenerating.front ? 'Generating...' : metadata.cover_image_url ? 'Regenerate (50 credits)' : 'Generate (50 credits)'}
              </button>
              <button
                className="upload-btn"
                onClick={() => handleCoverUpload('front')}
                disabled={uploadingCover.front}
              >
                {uploadingCover.front ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </section>

          {/* Back Cover Section */}
          <section className="cover-section">
            <h2>Back Cover</h2>

            <div className="cover-preview-container">
              <div className="cover-preview back-cover">
                {metadata.back_cover_image_url ? (
                  <div
                    className="cover-image-preview"
                    style={{ backgroundImage: `url(${metadata.back_cover_image_url})` }}
                  >
                    <div
                      className="cover-overlay-preview back zone-layout"
                      style={{ '--preview-text-color': COVER_COLORS.find(c => c.value === (metadata.back_cover_font_color || 'white'))?.hex || '#ffffff' } as React.CSSProperties}
                    >
                      <div className="preview-zone-top">
                        {metadata.back_cover_text_visible !== false && (metadata.back_cover_text_position || 'middle') === 'top' && (
                          <div className="preview-testimonial">
                            {metadata.back_cover_text || '"Sample testimonial text..."'}
                          </div>
                        )}
                        {metadata.author_bio_visible !== false && metadata.author_bio && (metadata.author_bio_position || 'middle') === 'top' && (
                          <div className="preview-author-bio">
                            <div className="preview-bio-heading">About the Author</div>
                            <div className="preview-bio-text">{metadata.author_bio}</div>
                          </div>
                        )}
                        {metadata.publishing_info_visible !== false && (metadata.isbn || metadata.publisher || metadata.copyright_text) && (metadata.publishing_info_position || 'bottom') === 'top' && (
                          <div className="preview-publishing-info">
                            {metadata.isbn && <div>ISBN: {metadata.isbn}</div>}
                            {metadata.publisher && <div>{metadata.publisher}</div>}
                            {metadata.copyright_text && <div>{metadata.copyright_text}</div>}
                          </div>
                        )}
                      </div>
                      <div className="preview-zone-middle">
                        {metadata.back_cover_text_visible !== false && (metadata.back_cover_text_position || 'middle') === 'middle' && (
                          <div className="preview-testimonial">
                            {metadata.back_cover_text || '"Sample testimonial text..."'}
                          </div>
                        )}
                        {metadata.author_bio_visible !== false && metadata.author_bio && (metadata.author_bio_position || 'middle') === 'middle' && (
                          <div className="preview-author-bio">
                            <div className="preview-bio-heading">About the Author</div>
                            <div className="preview-bio-text">{metadata.author_bio}</div>
                          </div>
                        )}
                        {metadata.publishing_info_visible !== false && (metadata.isbn || metadata.publisher || metadata.copyright_text) && (metadata.publishing_info_position || 'bottom') === 'middle' && (
                          <div className="preview-publishing-info">
                            {metadata.isbn && <div>ISBN: {metadata.isbn}</div>}
                            {metadata.publisher && <div>{metadata.publisher}</div>}
                            {metadata.copyright_text && <div>{metadata.copyright_text}</div>}
                          </div>
                        )}
                      </div>
                      <div className="preview-zone-bottom">
                        {metadata.back_cover_text_visible !== false && (metadata.back_cover_text_position || 'middle') === 'bottom' && (
                          <div className="preview-testimonial">
                            {metadata.back_cover_text || '"Sample testimonial text..."'}
                          </div>
                        )}
                        {metadata.author_bio_visible !== false && metadata.author_bio && (metadata.author_bio_position || 'middle') === 'bottom' && (
                          <div className="preview-author-bio">
                            <div className="preview-bio-heading">About the Author</div>
                            <div className="preview-bio-text">{metadata.author_bio}</div>
                          </div>
                        )}
                        {metadata.publishing_info_visible !== false && (metadata.isbn || metadata.publisher || metadata.copyright_text) && (metadata.publishing_info_position || 'bottom') === 'bottom' && (
                          <div className="preview-publishing-info">
                            {metadata.isbn && <div>ISBN: {metadata.isbn}</div>}
                            {metadata.publisher && <div>{metadata.publisher}</div>}
                            {metadata.copyright_text && <div>{metadata.copyright_text}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-cover-placeholder">
                    <p>No back cover</p>
                    <p className="placeholder-hint">Generate below</p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <div className="field-with-position">
                <label htmlFor="back_cover_text">Back Cover Text</label>
                <PositionSelector
                  value={(metadata.back_cover_text_position || 'middle') as TextPosition}
                  onChange={(pos) => updateField('back_cover_text_position', pos)}
                  visible={metadata.back_cover_text_visible !== false}
                  onVisibilityChange={(v) => updateField('back_cover_text_visible', v)}
                />
              </div>
              <textarea
                id="back_cover_text"
                rows={4}
                value={metadata.back_cover_text || ''}
                onChange={(e) => updateField('back_cover_text', e.target.value)}
                placeholder={`"A breathless, expertly-plotted debut..."\n— Sarah J. Hill`}
              />
              <small>Testimonials and quotes</small>
            </div>

            <div className="form-group">
              <div className="field-with-position">
                <label htmlFor="author_bio">Author Bio</label>
                <PositionSelector
                  value={(metadata.author_bio_position || 'middle') as TextPosition}
                  onChange={(pos) => updateField('author_bio_position', pos)}
                  visible={metadata.author_bio_visible !== false}
                  onVisibilityChange={(v) => updateField('author_bio_visible', v)}
                />
              </div>
              <textarea
                id="author_bio"
                rows={3}
                value={metadata.author_bio || ''}
                onChange={(e) => updateField('author_bio', e.target.value)}
                placeholder="Brief author biography..."
              />
            </div>

            <ColorSelector
              value={(metadata.back_cover_font_color || 'white') as CoverColor}
              onChange={(color) => updateField('back_cover_font_color', color)}
              label="Text Color"
            />

            <div className="form-group">
              <label htmlFor="back_cover_prompt">AI Image Prompt (Optional)</label>
              <textarea
                id="back_cover_prompt"
                rows={3}
                value={coverPrompts.back}
                onChange={(e) => setCoverPrompts(prev => ({ ...prev, back: e.target.value }))}
                placeholder="Generate AI prompt from memoir or leave empty for default..."
              />
            </div>

            <div className="cover-buttons-row">
              <button
                className="generate-btn"
                onClick={() => generatePrompt('back')}
                disabled={isGeneratingPrompt.back}
              >
                {isGeneratingPrompt.back ? 'Generating...' : 'AI Prompt (1 credit)'}
              </button>
              <button
                className="generate-btn"
                onClick={() => generateCover('back')}
                disabled={isGenerating.back}
              >
                {isGenerating.back ? 'Generating...' : metadata.back_cover_image_url ? 'Regenerate (50 credits)' : 'Generate (50 credits)'}
              </button>
              <button
                className="upload-btn"
                onClick={() => handleCoverUpload('back')}
                disabled={uploadingCover.back}
              >
                {uploadingCover.back ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </section>
        </div>

        {/* Additional Metadata Section */}
        <section className="metadata-section">
          <h2>Additional Publishing Information</h2>

          <div className="form-group">
            <label htmlFor="dedication">Dedication</label>
            <textarea
              id="dedication"
              rows={3}
              value={metadata.dedication || ''}
              onChange={(e) => updateField('dedication', e.target.value)}
              placeholder="Optional dedication text"
            />
            <small>Appears on a separate page after the front cover</small>
          </div>

          <div className="form-group">
            <div className="field-with-position">
              <label>Publishing Info (Back Cover)</label>
              <PositionSelector
                value={(metadata.publishing_info_position || 'bottom') as TextPosition}
                onChange={(pos) => updateField('publishing_info_position', pos)}
                visible={metadata.publishing_info_visible !== false}
                onVisibilityChange={(v) => updateField('publishing_info_visible', v)}
              />
            </div>
            <small>Position of ISBN, Publisher, and Copyright on back cover</small>
          </div>

          <div className="form-row isbn-publisher-row">
            <div className="form-group">
              <label htmlFor="isbn">ISBN</label>
              <input
                id="isbn"
                type="text"
                value={metadata.isbn || ''}
                onChange={(e) => updateField('isbn', e.target.value)}
                placeholder="978-0-00-000000-0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="publisher">Publisher</label>
              <input
                id="publisher"
                type="text"
                value={metadata.publisher || ''}
                onChange={(e) => updateField('publisher', e.target.value)}
                placeholder="Publisher name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="copyright_text">Copyright Text</label>
            <input
              id="copyright_text"
              type="text"
              value={metadata.copyright_text || ''}
              onChange={(e) => updateField('copyright_text', e.target.value)}
              placeholder="© 2025 Author Name. All rights reserved."
            />
          </div>
        </section>
      </div>

      <div className="publishing-actions">
        <button
          className="save-btn primary"
          onClick={saveMetadata}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Publishing Settings'}
        </button>
        <button
          className="cancel-btn"
          onClick={() => navigate(`/memoir/${memoirId}`)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PublishingSettings;
